// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Zap, 
  Lock, 
  Unlock, 
  Settings, 
  Check, 
  AlertCircle,
  Cpu,
  Globe,
  Key
} from 'lucide-react';
import './AISelector.css';

const AISelector = ({ 
  providers = {}, 
  selectedProvider, 
  selectedModel, 
  onProviderChange, 
  onModelChange,
  onSettingsOpen 
}) => {
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    gemini: '',
    claude: ''
  });

  // Clés LLM stockées en sessionStorage (et non localStorage) : effacées à la
  // fermeture de l'onglet → fenêtre d'exposition XSS réduite pour ces secrets.
  useEffect(() => {
    const savedKeys = sessionStorage.getItem('calibre_rag_api_keys');
    if (savedKeys) {
      try {
        setApiKeys(JSON.parse(savedKeys));
      } catch (e) {
        console.error('Erreur parsing clés API:', e);
      }
    }
  }, []);

  // Sauvegarder les clés API
  const saveApiKeys = (newKeys) => {
    setApiKeys(newKeys);
    sessionStorage.setItem('calibre_rag_api_keys', JSON.stringify(newKeys));
  };

  // Catégoriser les providers
  const freeProviders = Object.entries(providers).filter(([key, provider]) => 
    provider.type === 'local' || key === 'ollama'
  );
  
  const paidProviders = Object.entries(providers).filter(([key, provider]) => 
    provider.type === 'api' || ['openai', 'gemini', 'claude'].includes(key)
  );

  const getProviderIcon = (providerKey) => {
    switch (providerKey) {
      case 'ollama': return <Cpu className="provider-icon" />;
      case 'openai': return <Brain className="provider-icon" />;
      case 'gemini': return <Globe className="provider-icon" />;
      case 'claude': return <Zap className="provider-icon" />;
      default: return <Settings className="provider-icon" />;
    }
  };

  const getProviderStatus = (providerKey) => {
    if (['openai', 'gemini', 'claude'].includes(providerKey)) {
      return apiKeys[providerKey] ? 'configured' : 'missing_key';
    }
    return 'available';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'configured': return <Check className="status-icon configured" />;
      case 'missing_key': return <AlertCircle className="status-icon missing" />;
      case 'available': return <Unlock className="status-icon available" />;
      default: return <Lock className="status-icon locked" />;
    }
  };

  const handleProviderSelect = (providerKey) => {
    const provider = providers[providerKey];
    if (provider) {
      onProviderChange(providerKey);
      // Sélectionner le premier modèle disponible
      if (provider.models && provider.models.length > 0) {
        onModelChange(provider.models[0]);
      }
    }
  };

  const handleApiKeyChange = (provider, key) => {
    const newKeys = { ...apiKeys, [provider]: key };
    saveApiKeys(newKeys);
  };

  return (
    <div className="ai-selector">
      {/* En-tête */}
      <div className="ai-selector-header">
        <h3>🤖 Sélection Intelligence Artificielle</h3>
        <p>Choisissez votre modèle IA préféré</p>
      </div>

      {/* Providers gratuits */}
      <div className="provider-section">
        <div className="section-header">
          <Unlock className="section-icon" />
          <h4>IA Gratuites (Local)</h4>
          <span className="section-badge">Gratuit</span>
        </div>
        
        <div className="providers-grid">
          {freeProviders.map(([key, provider]) => (
            <div 
              key={key}
              className={`provider-card ${selectedProvider === key ? 'selected' : ''} ${getProviderStatus(key)}`}
              onClick={() => handleProviderSelect(key)}
            >
              <div className="provider-header">
                {getProviderIcon(key)}
                <div className="provider-info">
                  <h5>{provider.name || key}</h5>
                  <p>{provider.description || 'Modèle local'}</p>
                </div>
                {getStatusIcon(getProviderStatus(key))}
              </div>
              
              {provider.models && (
                <div className="provider-models">
                  {provider.models.map(model => (
                    <div 
                      key={model}
                      className={`model-option ${selectedModel === model ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onModelChange(model);
                      }}
                    >
                      {model}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Providers payants */}
      <div className="provider-section">
        <div className="section-header">
          <Lock className="section-icon" />
          <h4>IA Premium (API)</h4>
          <span className="section-badge">Payant</span>
        </div>
        
        <div className="providers-grid">
          {paidProviders.map(([key, provider]) => {
            const status = getProviderStatus(key);
            const hasKey = apiKeys[key];
            
            return (
              <div 
                key={key}
                className={`provider-card ${selectedProvider === key ? 'selected' : ''} ${status}`}
                onClick={() => hasKey && handleProviderSelect(key)}
              >
                <div className="provider-header">
                  {getProviderIcon(key)}
                  <div className="provider-info">
                    <h5>{provider.name || key}</h5>
                    <p>{provider.description || 'API externe'}</p>
                  </div>
                  {getStatusIcon(status)}
                </div>
                
                {provider.models && (
                  <div className="provider-models">
                    {provider.models.map(model => (
                      <div 
                        key={model}
                        className={`model-option ${selectedModel === model ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasKey) onModelChange(model);
                        }}
                      >
                        {model}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Configuration clé API */}
                {!hasKey && (
                  <div className="api-key-config">
                    <button 
                      className="configure-key-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowApiKeys(true);
                      }}
                    >
                      <Key className="key-icon" />
                      Configurer la clé API
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal configuration clés API */}
      {showApiKeys && (
        <div className="api-keys-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h4>🔑 Configuration des clés API</h4>
              <button 
                className="close-btn"
                onClick={() => setShowApiKeys(false)}
              >
                ×
              </button>
            </div>
            
            <div className="api-keys-form">
              <div className="api-key-group">
                <label>
                  <Brain className="key-icon" />
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  placeholder="sk-..."
                  value={apiKeys.openai}
                  onChange={(e) => handleApiKeyChange('openai', e.target.value)}
                />
                <small>Pour GPT-4, GPT-3.5-turbo</small>
              </div>
              
              <div className="api-key-group">
                <label>
                  <Globe className="key-icon" />
                  Google Gemini API Key
                </label>
                <input
                  type="password"
                  placeholder="AIza..."
                  value={apiKeys.gemini}
                  onChange={(e) => handleApiKeyChange('gemini', e.target.value)}
                />
                <small>Pour Gemini Pro, Gemini Flash</small>
              </div>
              
              <div className="api-key-group">
                <label>
                  <Zap className="key-icon" />
                  Anthropic Claude API Key
                </label>
                <input
                  type="password"
                  placeholder="sk-ant-..."
                  value={apiKeys.claude}
                  onChange={(e) => handleApiKeyChange('claude', e.target.value)}
                />
                <small>Pour Claude 3, Claude 3.5</small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="save-btn"
                onClick={() => setShowApiKeys(false)}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Informations */}
      <div className="ai-selector-info">
        <div className="info-card">
          <h5>💡 Conseils</h5>
          <ul>
            <li><strong>Gratuit :</strong> Ollama avec modèles locaux (pas de limite)</li>
            <li><strong>Payant :</strong> APIs externes (facturation selon usage)</li>
            <li><strong>Sécurité :</strong> Vos clés API restent sur votre machine</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AISelector;
